'use client';
import { useCallback, useRef, useState } from 'react';
import { useWallets } from '@privy-io/react-auth';
import { useQueryClient } from '@tanstack/react-query';
import { createWalletClient, custom, hexToBytes } from 'viem';
import { polygon } from 'viem/chains';

import { walletApi } from '@/services/api/wallet.api';
import { PORTFOLIO_QUERY_KEYS } from '@/services/query';
import type {
  DepositWalletTypedData,
  PrepareBridgeParams,
  PrepareResponse,
  WithdrawalStatus,
} from '@/types/withdrawal.types';

export type BridgeStep = 'idle' | 'preparing' | 'submitting' | 'polling' | 'completed' | 'failed';

export interface UseBridgeWithdrawalReturn {
  step: BridgeStep;
  withdrawalId: number | null;
  transactionHash: string | null;
  bridgeStatus: WithdrawalStatus | null;
  error: string | null;
  executeBridgeWithdrawal: (params: PrepareBridgeParams) => Promise<void>;
  reset: () => void;
}

const POLL_INTERVAL_MS = 10_000;
const MAX_POLLS = 60;

function isDepositWalletBridgePrepare(prepared: PrepareResponse): boolean {
  if (prepared.walletType === 'deposit_wallet') return true;
  return prepared.typedData.primaryType === 'Batch';
}

export function useBridgeWithdrawal(): UseBridgeWithdrawalReturn {
  const { wallets } = useWallets();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<BridgeStep>('idle');
  const [withdrawalId, setWithdrawalId] = useState<number | null>(null);
  const [transactionHash, setTxHash] = useState<string | null>(null);
  const [bridgeStatus, setBridgeStatus] = useState<WithdrawalStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollCount = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const reset = useCallback(() => {
    stopPolling();
    setStep('idle');
    setWithdrawalId(null);
    setTxHash(null);
    setBridgeStatus(null);
    setError(null);
    pollCount.current = 0;
  }, []);

  const startPolling = useCallback(
    (id: number) => {
      pollCount.current = 0;

      const poll = async () => {
        if (pollCount.current >= MAX_POLLS) {
          setError('Polling timed out. Check your withdrawal status manually.');
          setStep('failed');
          return;
        }
        pollCount.current += 1;

        try {
          const status = await walletApi.getBridgeStatus(id);
          setBridgeStatus(status.status);

          if (status.status === 'completed') {
            setTxHash(status.transactionHash ?? null);
            setStep('completed');
            // Same explicit-invalidation pattern as useClobOrder.ts (post-trade) and
            // DepositModal.tsx (post-deposit) — the header/portfolio balance query's
            // background poll is intentionally slow, so a balance-changing action
            // must refresh it explicitly rather than wait on that interval.
            queryClient.invalidateQueries({ queryKey: PORTFOLIO_QUERY_KEYS.all });
            return;
          }

          if (status.status === 'failed') {
            setError('Bridge reported a failure.');
            setStep('failed');
            return;
          }

          pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to poll status';
          setError(message);
          setStep('failed');
        }
      };

      poll();
    },
    [queryClient],
  );

  const executeBridgeWithdrawal = useCallback(
    async (params: PrepareBridgeParams) => {
      try {
        setError(null);
        setStep('preparing');

        // Step 1: Backend prepares unsigned EIP-712 SafeTx + stores DB record
        const prepared = await walletApi.prepareBridgeWithdrawal(params);
        console.log('prepared', prepared);
        setWithdrawalId(prepared.withdrawalId);

        setStep('submitting');

        // Step 2: User signs with the available connected wallet
        const signingWallet =
          wallets.find((w) => w.walletClientType === 'privy' || w.connectorType === 'embedded') ||
          wallets[0];
        if (!signingWallet) {
          throw new Error('Connected wallet not found. Please log in.');
        }

        await signingWallet.switchChain(137);

        const ethereumProvider = await signingWallet.getEthereumProvider();
        const walletClient = createWalletClient({
          account: signingWallet.address as `0x${string}`,
          chain: polygon,
          transport: custom(ethereumProvider),
        });

        let signature: `0x${string}`;

        if (isDepositWalletBridgePrepare(prepared)) {
          console.log('prepared1', prepared);
          const td = prepared.typedData as DepositWalletTypedData;
          console.log('td', td);
          const message = {
            wallet: td.message.wallet as `0x${string}`,
            nonce: BigInt(String(td.message.nonce)),
            deadline: BigInt(String(td.message.deadline)),
            calls: td.message.calls.map((c) => ({
              target: c.target as `0x${string}`,
              value: BigInt(String(c.value)),
              data: c.data as `0x${string}`,
            })),
          };

          signature = await walletClient.signTypedData({
            account: signingWallet.address as `0x${string}`,
            domain: {
              name: td.domain.name,
              version: td.domain.version,
              chainId: BigInt(td.domain.chainId),
              verifyingContract: td.domain.verifyingContract as `0x${string}`,
            },
            types: {
              Call: [...td.types.Call],
              Batch: [...td.types.Batch],
            },
            primaryType: 'Batch',
            message,
          });
        } else {
          // Legacy Gnosis Safe: personal_sign on raw EIP-712 struct hash (eth_sign path)
          if (!prepared.structHash) {
            throw new Error(
              'Backend did not return structHash. Update the backend to the latest version.',
            );
          }
          console.log('account', signingWallet.address as `0x${string}`);
          console.log('raw', hexToBytes(prepared.structHash));

          signature = await walletClient.signMessage({
            account: signingWallet.address as `0x${string}`,
            message: { raw: hexToBytes(prepared.structHash) },
          });
        }
        console.log('signature', signature);
        // Step 3: Send signature to backend — backend uses HMAC to submit to relayer
        // This avoids exposing builder credentials to the browser (was causing 401).
        const submitResult = await walletApi.submitBridgeWithdrawal(
          prepared.withdrawalId,
          signature,
          prepared.meta,
        );

        setTxHash(submitResult.transactionHash ?? null);
        setBridgeStatus('bridging');

        setStep('polling');
        startPolling(prepared.withdrawalId);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        setStep('failed');
      }
    },
    [wallets, startPolling],
  );

  return {
    step,
    withdrawalId,
    transactionHash,
    bridgeStatus,
    error,
    executeBridgeWithdrawal,
    reset,
  };
}
